import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { ConcentrateSubType, ForageSubType } from 'src/assets/data/enum/rationSubType';
import { Ration, RationProduct } from '../../_models/rations/rations';
import { ProductService } from '../product.service';
import { environment } from '../../../../environments/environment';

export const FORAGES_SUBTYPES: string[] = Object.values(ForageSubType);
export const CONCENTRATES_SUBTYPES: string[] = Object.values(ConcentrateSubType);

@Injectable()
export class RationsService {
  private apiBaseUrl = environment.rationServiceUrl;

  constructor(
    private http: HttpClient,
    private productService: ProductService
  ) {}

  static getForagesSubtypes(): string[] {
    return FORAGES_SUBTYPES;
  }
  static getConcentratesSubtypes(): string[] {
    return CONCENTRATES_SUBTYPES;
  }

  get(year: number): Observable<Ration[]> {
    return this.http
      .get<Ration[]>(`${this.apiBaseUrl}api/private/v1/rations/plans`, {
        params: new HttpParams().set('year', year.toString()),
      })
      .pipe(map((response) => response));
  }

  getById(id: string): Observable<Ration> {
    return this.http
      .get<Ration>(`${this.apiBaseUrl}api/private/v1/rations/plans/${id}`)
      .pipe(map((response) => response));
  }

  getDistributionById(distributionPayload): Observable<any> {
    const params = new HttpParams()
      .set('from', distributionPayload.from)
      .set('to', distributionPayload.to);

    return this.http
      .get<any>(
        `${this.apiBaseUrl}api/private/v1/rations/plans/${distributionPayload.id}/distribution`,
        { params }
      )
      .pipe(map((response) => response));
  }

  getExport(body, type): Observable<any> {
    let params = new HttpParams().set('type', 'RationsPlan').set('subtype', 'RationsPlan');

    if (type) {
      params = params.set('format', type);
    }

    return this.http
      .post(`${this.apiBaseUrl}api/private/v1/rations/plans/export`, body, {
        params,
        responseType: 'blob' as 'json',
      })
      .pipe(
        map((res) => res),
        catchError((err) => {
          throw new Error(err);
        })
      );
  }

  save(ration: Ration): Observable<Ration> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    ration.companyId = companySelected ? companySelected.id : null;

    return this.http
      .post<Ration>(`${this.apiBaseUrl}api/private/v1/rations/plans`, ration)
      .pipe(map((response) => response));
  }

  edit(ration: Ration): Observable<Ration> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    ration.companyId = companySelected ? companySelected.id : null;

    return this.http
      .put<Ration>(`${this.apiBaseUrl}api/private/v1/rations/plans/${ration.id}`, ration)
      .pipe(map((response) => response));
  }

  statistics(ration: Ration): Observable<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    ration.companyId = companySelected ? companySelected.id : null;

    return this.http
      .post<any>(`${this.apiBaseUrl}api/private/v1/rations/plans/statistics`, ration)
      .pipe(map((response) => response));
  }

  saveProductStatistics(ration: Ration): Observable<RationProduct[]> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    ration.companyId = companySelected ? companySelected.id : null;

    return this.http
      .post<
        RationProduct[]
      >(`${this.apiBaseUrl}api/private/v1/rations/plans/productStatistics`, ration)
      .pipe(map((response) => response));
  }

  delete(ration: Ration): Observable<Ration> {
    return this.http
      .delete<Ration>(`${this.apiBaseUrl}api/private/v1/rations/plans/${ration.id}`)
      .pipe(map(() => ({ id: ration.id }) as Ration));
  }

  revoke(ration: Ration): Observable<Ration> {
    return this.http
      .put<Ration>(`${this.apiBaseUrl}api/private/v1/rations/plans/${ration.id}/revoke`, ration)
      .pipe(map(() => ({ id: ration.id }) as Ration));
  }

  getForagesMixes(mixes: RationProduct[]): RationProduct[] {
    return mixes.filter(
      (mix) => mix.product && mix.product.typology && mix.product.typology === 'forages'
    );
  }

  getConcentratesMixes(mixes: RationProduct[]): RationProduct[] {
    return mixes.filter(
      (mix) =>
        mix.product &&
        mix.product.typology &&
        (mix.product.typology === 'concentrates' || mix.product.typology === 'supplements')
    );
  }

  getMixesBySubtype(mixes: RationProduct[], subtype: string): RationProduct[] {
    return mixes.filter((mix) => mix.activityType === subtype);
  }

  // not used anywhere
  getForagesConcentratesRatio(mixes: RationProduct[]): number {
    const forages = this.getForagesMixes(mixes);
    const concentrates = this.getConcentratesMixes(mixes);

    const foragesDrySubstance = forages.reduce(
      (sum, mix) => sum + RationsService.getDrySubstanceQuantity(mix),
      0
    );
    const concentratesDrySubstance = concentrates.reduce(
      (sum, mix) => sum + RationsService.getDrySubstanceQuantity(mix),
      0
    );

    return concentratesDrySubstance ? foragesDrySubstance / concentratesDrySubstance : 0;
  }

  // not used anywhere
  getMolassesDrySubstanceRatio(mixes: RationProduct[]): number {
    const forages = this.getForagesMixes(mixes);
    const concentrates = this.getConcentratesMixes(mixes);
    const molasses = this.getMixesBySubtype(mixes, 'molasses');

    const allDrySubstance = [...forages, ...concentrates].reduce(
      (sum, mix) => sum + RationsService.getDrySubstanceQuantity(mix),
      0
    );
    const molassesDrySubstance = molasses.reduce(
      (sum, mix) => sum + RationsService.getDrySubstanceQuantity(mix),
      0
    );

    return allDrySubstance ? molassesDrySubstance / allDrySubstance : 0;
  }

  static getDrySubstanceQuantity(mix: RationProduct): number {
    return mix.dose && mix.dryMatter ? mix.dose * (mix.dryMatter / 100) : 0;
  }

  checkActiveRations(rations: Ration[], selectedYear: number): Ration[] {
    const currentDate = moment();
    const currentYear = new Date().getFullYear();

    return rations.filter((ration) => {
      const existsFutureDate = ration.timeSpan.some((ts) =>
        currentDate.isSameOrBefore(moment(ts.end))
      );
      return selectedYear >= currentYear && existsFutureDate;
    });
  }

  checkNotActiveRations(rations: Ration[], selectedYear: number): Ration[] {
    const currentDate = moment();
    const currentYear = new Date().getFullYear();

    return rations.filter((ration) => {
      const allDatesArePast = ration.timeSpan.every((ts) => currentDate.isAfter(moment(ts.end)));
      return selectedYear < currentYear || allDatesArePast;
    });
  }

  plansRationsList() {
    return ['active_plans', 'inactive_plans'];
  }
}
